import React from "react";
import {
  FieldError,
  FieldErrorsImpl,
  Merge,
  RegisterOptions,
} from "react-hook-form";
import { UseFormRegister } from "react-hook-form";
import { FormattedMessage, useIntl, useLocale } from "@/i18n/i18n-client";
import FormField, { FormFieldProps } from "../FormField";

interface EmailInputFieldProps
  extends React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  > {
  register: UseFormRegister<any>;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
  fieldName?: string;
  registerOptions?: RegisterOptions;
  label?: React.ReactNode;
  formFieldProps?: Omit<FormFieldProps, "children">;
  icon?: React.ReactNode;
  showStrick?: boolean;
}

const specialCharRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const EmailInputField = ({
  register,
  error,
  fieldName = "email",
  label = <FormattedMessage defaultMessage="Email" id="sy+pv5" />,
  formFieldProps,
  icon,
  showStrick,
  ...props
}: EmailInputFieldProps) => {
  const intl = useIntl();
  const { dir } = useLocale();
  const isRtl = dir === "rtl";
  return (
    <FormField
      label={label}
      id={fieldName}
      error={error}
      showStrick={showStrick}
      {...formFieldProps}
    >
      <div className="relative w-full">
        {/* Left Icon */}
        {icon && (
          <span
            className={`absolute ${!isRtl ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 text-gray-500 flex items-center`}
          >
            {icon}
          </span>
        )}
        <input
          {...register(fieldName, {
            ...props.registerOptions,
            validate: (value, formValues) => {
              const trimmedValue = value?.trim();
              if (trimmedValue?.length === 0 && !formFieldProps?.isOptional) {
                return intl.formatMessage({
                  defaultMessage: "Email is required",
                  id: "cU/aSG",
                });
              }
              if (
                !specialCharRegex.test(trimmedValue) &&
                trimmedValue?.length > 0
              ) {
                return intl.formatMessage({
                  defaultMessage: "Email is not valid",
                  id: "8vbtx1",
                });
              }
              if (props.registerOptions?.validate) {
                const validateFn = props.registerOptions.validate;
                if (typeof validateFn === "function") {
                  return validateFn(value, formValues);
                }
              }
              return true;
            },
          })}
          {...props}
          id={fieldName}
          className={`field-input ${error ? "invalid" : ""} ${
            icon ? (isRtl ? "pr-10" : "pl-10") : ""
          } ${props.className}`}
          placeholder={
            props.placeholder ||
            intl.formatMessage({
              defaultMessage: "Enter your email",
              id: "5MDGuM",
            })
          }
        />
      </div>
    </FormField>
  );
};

export default EmailInputField;
